/**
 * Monetization funnel event names and their properties (PPL-351).
 *
 * The funnel is `paywall_viewed → upgrade_cta_clicked → checkout_started →
 * checkout_completed`, and it only works if both clients and the server agree on
 * the strings. The server half already fires `gate_hit`, `checkout_started`
 * (PayFast) and `subscription_cancelled` from `cloud-functions/functions/index.js`;
 * these are the names the CLIENTS use, and `app/src/utils/monetizationEvents.ts`
 * is a deliberate mirror of this file. Change one, change both.
 *
 * ## Why `source` matters more than it looks
 *
 * A single journey legitimately produces TWO `paywall_viewed` events — the
 * at-limit sheet, then the premium screen the user lands on after tapping through.
 * That is not double-counting: they really did see two paywalls. `source`
 * distinguishes them, and the funnel is read per-person rather than per-event, so
 * the extra view does not break the step. Without `source` the two are
 * indistinguishable and "which surface actually sells" is unanswerable — which is
 * the question this instrumentation exists for.
 *

 * ## `source` is WHO FIRED IT, `referrer_source` is who sent them
 *
 * These are different questions and one property cannot answer both. Tagging the
 * premium screen's own view with the sheet's name makes the two steps of a gated
 * journey indistinguishable, and leaves the `premium_screen` bucket holding only
 * direct visits — so "which surface actually sells" becomes unanswerable, which
 * is the one thing this is for. Every event carries the surface that emitted it;
 * `referrer_source` carries the origin when there was one.
 *
 * ## `gate` rides along wherever a limit sent them
 *
 * Same reasoning the server uses for `gate_hit`: one property rather than an
 * event per limit, so "which limit drives upgrades" is a single breakdown.
 *
 * ## What is deliberately NOT here
 *
 * `gate_hit` is server-side only. `setPersonRememberedEurW` fires it when it
 * refuses, and firing a client copy as well would double-count the one funnel
 * number the tier decision rests on.
 */

export const MonetizationEvents = {
  /** A paywall surface came into view. Carries `source`. */
  PAYWALL_VIEWED: 'paywall_viewed',
  /** Any button whose job is to start an upgrade. Carries `source`, maybe `gate`. */
  UPGRADE_CTA_CLICKED: 'upgrade_cta_clicked',
  /**
   * The store/checkout flow actually began.
   *
   * NOT fired by this client. `createWebCheckoutEurW` emits it server-side after
   * the PayFast URL is generated, so a failed checkout creation is not counted as
   * an intent to pay — and a client copy would double the step. Listed here only
   * so the name is in one place; the app DOES fire its own, because the
   * RevenueCat purchase has no server moment to hang it off.
   */
  CHECKOUT_STARTED: 'checkout_started',
  /** The user backed out of the store sheet without buying. */
  CHECKOUT_ABANDONED: 'checkout_abandoned',
  /**
   * The premium surface could not resolve the visitor's account state.
   * Mirrors the app's constant (PPL-353 review parity) so the failure is one
   * breakdown across surfaces.
   */
  PAYWALL_LOAD_FAILED: 'paywall_load_failed',
  /** The day-14 takeover rendered its first beat (PPL-358). */
  DOWNGRADE_FLOW_VIEWED: 'downgrade_flow_viewed',
  /** The picker's choice committed. Carries `kept` and `eligible`. */
  PEOPLE_KEPT_SELECTED: 'people_kept_selected',
  /** The takeover's escape-hatch CTA — "remember everyone". */
  DOWNGRADE_UPGRADE_TAPPED: 'downgrade_upgrade_tapped',
} as const;

/**
 * Which sell state the premium surface was in when a CTA fired (PPL-352/353).
 *
 * A closed set for the same reason as PaywallSource: these become PostHog
 * breakdown values, and "which state actually sells" is the question the
 * repackage exists to answer. Mirrors the app's PaywallSellState.
 */
export const PaywallSellState = {
  DEFAULT: 'default',
  AT_CAP: 'atCap',
  TRIALING: 'trialing',
  LAPSED: 'lapsed',
} as const;

export type PaywallSellStateValue = typeof PaywallSellState[keyof typeof PaywallSellState];

/**
 * Where a paywall event came from.
 *
 * Kept as a closed set rather than free strings: these become PostHog breakdown
 * values, and a typo silently creates a second bucket that looks like a real
 * surface with suspiciously low traffic.
 */
export const PaywallSource = {
  /** The at-limit sheet — the most important paywall in the product. */
  REMEMBERED_LIMIT_SHEET: 'remembered_limit_sheet',
  /** The once-shown "your premium ended" banner on the ppl list. */
  LAPSE_BANNER: 'lapse_banner',
  /** The premium screen/page itself — every view of it, however they arrived. */
  PREMIUM_SCREEN: 'premium_screen',
  /** The trial countdown chip in the chrome (PPL-358). */
  TRIAL_COUNTDOWN: 'trial_countdown',
  /** The day-14 downgrade takeover (PPL-358). */
  DOWNGRADE_TAKEOVER: 'downgrade_takeover',
} as const;

/** Limits that can send someone to a paywall. Mirrors the server's `gate_hit`. */
export const PaywallGate = {
  REMEMBERED_PEOPLE: 'remembered_people',
} as const;

export type PaywallSourceValue = typeof PaywallSource[keyof typeof PaywallSource];
export type PaywallGateValue = typeof PaywallGate[keyof typeof PaywallGate];

/**
 * Coerce an externally-supplied source (a route/query param) to a known one.
 *
 * The closed set exists so a typo cannot mint a bucket that looks like a real
 * surface with suspiciously low traffic — and a value arriving from a URL is not
 * merely typo-prone, it is attacker- and crawler-supplied. `/premium?source=x`
 * would otherwise put `x` straight into the PostHog breakdown.
 */
export function coercePaywallSource(raw: unknown): PaywallSourceValue | null {
  const values = Object.values(PaywallSource) as string[];
  return typeof raw === 'string' && values.includes(raw) ? (raw as PaywallSourceValue) : null;
}

/** Same, for the gate that sent them. */
export function coercePaywallGate(raw: unknown): PaywallGateValue | null {
  const values = Object.values(PaywallGate) as string[];
  return typeof raw === 'string' && values.includes(raw) ? (raw as PaywallGateValue) : null;
}
